import { Row, Col, Switch, Typography, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { SingleSelect } from "../General/Select";
import { useSelector } from "react-redux";
import { memo } from "react";
import DispatchText from "./dispatchText";

const { Text, Title } = Typography;

const AvailabilityModeEditor = ({ value, onValueChange }) => {
  const isOfficeHours = value === "office_hours";

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "10px"
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "4px"
      }}>
        <Text type="secondary" strong underline>
          Office Hours Only
        </Text>
        <Tooltip
          title="Off keeps this job bookable 24/7, regardless of the organization's office hours. On restricts this job to the organization's office hours, set once in Capacity Planning."
          placement="top"
          overlayStyle={{ maxWidth: "380px" }}
        >
          <InfoCircleOutlined
            style={{ color: "#9ca3af", fontSize: "14px", cursor: "help" }}
          />
        </Tooltip>
      </div>
      <Switch
        size="middle"
        checked={isOfficeHours}
        onChange={(checked) =>
          onValueChange(checked ? "office_hours" : "24/7")
        }
      />
    </div>
  );
};

const JobMapping = ({
  jobId,
  mappedData,
  filterOption,
  onChange,
}) => {
  const { serviceTitanBusinessUnits, serviceTitanJobs, organizationData } = useSelector(
    (state) => state?.organization
  );

  const isHCP = organizationData?.organization_provider === 'HCP';

  return (
    <div style={{
      width: "100%",
      background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
      border: "1px solid #e2e8f0",
      borderRadius: "12px",
      padding: "24px",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        marginBottom: "24px",
        paddingBottom: "16px",
        borderBottom: "2px solid #e2e8f0"
      }}>
        <div style={{
          width: "4px",
          height: "24px",
          background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
          borderRadius: "2px",
          marginRight: "12px"
        }} />
        <Title level={5} style={{ margin: 0, color: "#1e293b", fontWeight: 600 }}>
          Job Mapping Configuration
        </Title>
      </div>

      <div style={{ marginBottom: "24px" }}>
        <Text type="secondary" style={{
          fontSize: "14px",
          fontWeight: 600,
          color: "#64748b",
          marginBottom: "16px",
          display: "block"
        }}>
          ServiceTitan Configuration
        </Text>
        <Row gutter={[24, 16]} align="stretch">
          {!isHCP && (
            <Col xs={24} md={12} lg={8}>
              <div style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "16px",
                height: "100%",
                boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)"
              }}>
                <SingleSelect
                  title="Business Unit"
                  showSearch
                  placeholder="Select Business Unit"
                  value={mappedData?.st_business_unit_id}
                  onChange={(e) => {
                    const selectedST = serviceTitanBusinessUnits.find(
                      (val) => val.id === e
                    );
                    onChange(
                      {
                        st_business_unit_id: e,
                        st_business_unit_name: selectedST?.name || "",
                      },
                      jobId
                    );
                  }}
                  filterOption={filterOption}
                  options={serviceTitanBusinessUnits?.map((item) => ({
                    label: item.name,
                    value: item.id,
                  }))}
                  size="middle"
                  style={{ width: "100%" }}
                />
              </div>
            </Col>
          )}

          <Col xs={24} md={12} lg={isHCP ? 12 : 8}>
            <div style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              padding: "16px",
              height: "100%",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)"
            }}>
              <SingleSelect
                title={isHCP ? "Job Type" : "Non-Member Job Type"}
                showSearch
                placeholder={isHCP ? "Select Job Type" : "Select Non-Member Job Type"}
                value={mappedData?.st_non_member_job_id}
                onChange={(e) => {
                  const selectedJobItem = serviceTitanJobs.find((val) => val.id === e);
                  onChange(
                    {
                      st_non_member_job_id: e,
                      st_non_member_job_name: selectedJobItem?.name || "",
                    },
                    jobId
                  );
                }}
                filterOption={filterOption}
                options={serviceTitanJobs?.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
                size="middle"
                style={{ width: "100%" }}
              />
            </div>
          </Col>

        </Row>
      </div>


      <div>
        {
          !isHCP && (
            <Text type="secondary" style={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#64748b",
              marginBottom: "16px",
              display: "block"
            }}>
              Membership Configuration
            </Text>
          )
        }
        <Row gutter={[24, 16]} align="stretch">
          {!isHCP && (
            <>
              <Col xs={24} md={12} lg={12}>
                <div style={{
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "16px",
                  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)"
                }}>
                  <Row gutter={[12, 12]} align="middle">
                    <Col xs={24} sm={24} md={6}>
                      <div style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        maxWidth: "98px",
                        gap: "6px",
                        padding: "8px 12px",
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        height: "100%",
                        minHeight: "48px"
                      }}>
                        <Text type="secondary" style={{ whiteSpace: "nowrap" }} strong underline>
                          Is Member
                        </Text>
                        <Switch
                          size="middle"
                          checked={mappedData?.is_member}
                          onChange={(e) => {
                            const updateData = { is_member: e };
                            if (!e && mappedData?.st_non_member_job_id) {
                              updateData.st_member_job_id = mappedData.st_non_member_job_id;
                              updateData.st_member_job_name = mappedData.st_non_member_job_name;
                            }

                            onChange(updateData, jobId);
                          }}
                        />
                      </div>
                    </Col>

                    <Col xs={24} sm={24} md={18}>
                      <SingleSelect
                        title={mappedData?.is_member ? "Member Job Type" : "Member Job Type"}
                        showSearch
                        placeholder="Select Member Job Type"
                        value={mappedData?.is_member ? mappedData?.st_member_job_id : mappedData?.st_non_member_job_id}
                        onChange={(e) => {
                          const selectedJobItem = serviceTitanJobs.find(
                            (val) => val.id === e
                          );
                          onChange(
                            {
                              st_member_job_id: e,
                              st_member_job_name: selectedJobItem?.name || "",
                            },
                            jobId
                          );
                        }}
                        filterOption={filterOption}
                        options={serviceTitanJobs?.map((item) => ({
                          label: item.name,
                          value: item.id,
                        }))}
                        size="middle"
                        style={{ width: "100%" }}
                        disabled={!mappedData?.is_member}
                      />
                      {!mappedData?.is_member && (
                        <div style={{
                          fontSize: "12px",
                          color: "#64748b",
                          marginTop: "4px",
                          fontStyle: "italic"
                        }}>
                          Automatically uses Non-Member Job Type
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              </Col>

              <Col xs={24} sm={12} md={12} lg={6}>
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "10px",
                  padding: "12px 12px",
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                  transition: "all 0.2s ease"
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "2px",
                    textAlign: "center"
                  }}>
                    <Text type="secondary" strong underline>
                      Enable Recurring Service Deduction
                    </Text>
                    <Tooltip
                      title={`Make sure you have recurring events configured in ServiceTitan for Business Unit: "${mappedData?.st_business_unit_name || 'Not selected'}" and Job Type: "${mappedData?.is_member ? mappedData?.st_member_job_name : mappedData?.st_non_member_job_name || 'Not selected'}"`}
                      placement="top"
                      overlayStyle={{ maxWidth: "400px" }}
                    >
                      <InfoCircleOutlined
                        style={{
                          color: "#9ca3af",
                          fontSize: "14px",
                          cursor: "help"
                        }}
                      />
                    </Tooltip>
                  </div>
                  <Switch
                    size="middle"
                    checked={mappedData?.enabled_recurring_service}
                    onChange={(e) => onChange({ enabled_recurring_service: e }, jobId)}
                  />
                </div>
              </Col>
            </>
          )}
        </Row>
      </div>

      <div style={{ marginTop: "24px" }}>
        <Text type="secondary" style={{
          fontSize: "14px",
          fontWeight: 600,
          color: "#64748b",
          marginBottom: "16px",
          display: "block"
        }}>
          Availability
        </Text>

        <div style={{
          width: "fit-content",
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          padding: "20px",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)"
        }}>
          <AvailabilityModeEditor
            value={mappedData?.availability_mode}
            onValueChange={(value) => onChange({ availability_mode: value }, jobId)}
          />
        </div>
      </div>

      <div style={{ marginTop: "24px" }}>
        <Text type="secondary" style={{
          fontSize: "14px",
          fontWeight: 600,
          color: "#64748b",
          marginBottom: "16px",
          display: "block"
        }}>
          Dispatch Configuration
        </Text>


        <div style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          padding: "20px",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)"
        }}>
          <DispatchText
            id={jobId}
            member_dispatch_fee={
              organizationData?.organization_provider === 'HCP' ? 0 : mappedData?.member_dispatch_fee
            }
            dispatch_fee={mappedData?.dispatch_fee}
            isDefault={mappedData?.use_default_dispatch}
            header={mappedData?.dispatch_header}
            body={mappedData?.dispatch_body}
            checkboxText={mappedData?.dispatch_text}
            unit_price={mappedData?.unit_price}
            description={mappedData?.description}
            onChange={onChange}
          />
        </div>
      </div>
    </div>
  );
};

export default memo(JobMapping);
